import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    CardActions,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemText,
    Typography,
    styled
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import DialogWrapper from '@libComponents/Dialog';
import { Button } from '@libComponents';
import { setSnack } from 'utilities/redux.actions';
import { exportHolidaysXlsx } from 'holidays/utils/holidayExcel';

const StyledCardAction = styled(CardActions)(({ theme: { spacing } }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: spacing(3),
    paddingBottom: 0
}));

const SummaryRow = ({ label, value }) => (
    <ListItem disablePadding sx={{ py: 0.5 }}>
        <ListItemText
            primary={<Typography variant='subtitle2'>{label}</Typography>}
        />
        <ListItemText
            sx={{ textAlign: 'right' }}
            primary={<Typography variant='body2'>{value}</Typography>}
        />
    </ListItem>
);

/**
 * "Export Excel" for the holiday list. A plain confirmation popup — it reuses
 * the same public API the table reads, applying the page's current filters
 * (year + calendar), then downloads the workbook.
 */
export default function ExportHolidaysExcel({
    year,
    calenderId = null,
    totalRows = 0
}) {
    const [open, setOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [progress, setProgress] = useState('');
    const dispatch = useDispatch();

    const handleClose = () => {
        if (!exporting) {
            setOpen(false);
            setProgress('');
        }
    };

    const handleExport = () => {
        if (exporting) return;
        setExporting(true);
        setProgress('Preparing export…');
        exportHolidaysXlsx({
            year,
            calenderId,
            onProgress: setProgress,
            onError: (message) => {
                dispatch(setSnack({ message, type: 'error' }));
                setProgress('');
            },
            onSuccess: () => {
                setOpen(false);
                setProgress('');
                dispatch(
                    setSnack({
                        message: 'Holidays Excel downloaded.',
                        type: 'success'
                    })
                );
            }
        }).finally(() => setExporting(false));
    };

    return (
        <>
            <Button
                variant='outlined'
                size='small'
                color='primary'
                type='button'
                onClick={() => setOpen(true)}
                startIcon={<FileDownloadOutlinedIcon color='primary' />}
                sx={{
                    height: '34px',
                    minHeight: '34px',
                    width: 'auto',
                    px: 2,
                    whiteSpace: 'nowrap'
                }}
            >
                Export Excel
            </Button>

            <DialogWrapper
                open={open}
                onClose={handleClose}
                sx={{ textAlign: 'center' }}
            >
                <Typography variant='h6' sx={{ mb: 1 }}>
                    Export Holidays
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ textAlign: 'left', px: 2 }}>
                    <Typography variant='body2' color='text.secondary'>
                        The Excel file will contain the holidays matching your
                        current filters:
                    </Typography>

                    <List sx={{ mt: 1 }}>
                        <SummaryRow label='Year' value={year || 'All years'} />
                        <SummaryRow
                            label='Calendar'
                            value={
                                calenderId
                                    ? 'Selected calendar only'
                                    : 'All calendars'
                            }
                        />
                        <SummaryRow
                            label='Holidays'
                            value={`${totalRows} record${
                                totalRows === 1 ? '' : 's'
                            }`}
                        />
                    </List>

                    {exporting && progress && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 1
                            }}
                        >
                            <CircularProgress size={16} />
                            <Typography
                                variant='caption'
                                color='text.secondary'
                            >
                                {progress}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Divider sx={{ mt: 2 }} />
                <StyledCardAction>
                    <Button
                        onClick={handleClose}
                        color='tertiary'
                        disabled={exporting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleExport}
                        loading={exporting}
                        disabled={!totalRows}
                    >
                        Export Excel
                    </Button>
                </StyledCardAction>
            </DialogWrapper>
        </>
    );
}
